import React, { useState } from "react";
import PropTypes from "prop-types";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import ListItemButton from "@mui/material/ListItemButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { MdChevronRight, MdExpandMore, MdSearch, MdVisibility, MdVisibilityOff, MdWarningAmber } from "react-icons/md";
import { avatarColor, initialOf } from "../../design/ui";

/**
 * 專案共用的 MUI 組合元件。
 *
 * 原本的 .el-tag / .el-banner / .el-row / .el-progress / .el-disclosure 各有一組
 * 「狀態 → 顏色」的對應，換成 MUI 之後集中在這裡，各頁只講語意（kind / tone），不自己挑顏色。
 *
 * kind／tone 與 MUI 顏色的對應：
 *   attention → error（紅：逾期、失敗、高風險）
 *   warn      → warning（黃：快到期、中風險、提醒）
 *   progress  → primary（藍：進行中、資訊）
 *   info      → info
 *   done      → success（綠：完成、通過）
 *   closed    → grey（灰：未開始、已關閉、沒有）
 *   type      → 外框（類型標籤，不帶狀態色）
 */
const COLOR = {
  attention: "error",
  warn: "warning",
  progress: "primary",
  info: "info",
  done: "success",
  closed: "grey",
};

/** 列左側色帶：tone → 主題色 */
const BAND = { attention: "error", warn: "warning", done: "success", closed: "grey" };
const bandSx = (tone) => {
  const c = BAND[tone];
  if (!c) return {};
  return { boxShadow: (t) => `inset 4px 0 0 ${c === "grey" ? t.palette.grey[500] : t.palette[c].main}` };
};

const softSx = (kind) => {
  const c = COLOR[kind];
  if (!c) return {};
  if (c === "grey") return { bgcolor: "grey.100", color: "grey.700" };
  return { bgcolor: `${c}.light`, color: `${c}.dark` };
};

/**
 * 狀態標籤（取代 .el-tag）。一定帶文字，不能只靠顏色。
 * <StatusChip kind="done" mark={<MdCheck />}>已完成</StatusChip>
 */
export function StatusChip({ kind, mark, children, size, sx, ...rest }) {
  if (kind === "type") {
    return <Chip variant="outlined" label={children} size={size} sx={{ fontWeight: 400, ...sx }} {...rest} />;
  }
  return (
    <Chip
      icon={mark ? <Box component="span" sx={{ display: "inline-flex", color: "inherit !important" }}>{mark}</Box> : undefined}
      label={children}
      size={size}
      sx={{ ...softSx(kind), "& .MuiChip-icon": { color: "inherit" }, ...sx }}
      {...rest}
    />
  );
}

StatusChip.propTypes = {
  kind: PropTypes.oneOf(["attention", "warn", "progress", "info", "done", "closed", "type"]),
  mark: PropTypes.node,
  children: PropTypes.node,
  size: PropTypes.oneOf(["small", "medium"]),
  sx: PropTypes.object,
};
StatusChip.defaultProps = { kind: "closed", mark: null, children: null, size: "medium", sx: {} };

/**
 * 提示條（取代 .el-banner）。kind：warn / attention / info / done。
 * icon 不傳就用 MUI 預設的圖示；傳 false 就不畫圖示。
 * role 預設 "note"（靜態說明，不會被讀螢幕主動念出）；要即時提醒時明確傳 role="alert" 或 "status"。
 */
const SEVERITY = { warn: "warning", attention: "error", info: "info", done: "success", progress: "info" };

export function Banner({ kind, icon, action, title, role, children, sx, ...rest }) {
  return (
    <Alert severity={SEVERITY[kind] || "info"} icon={icon} action={action} role={role} sx={sx} {...rest}>
      {title ? (
        <Typography component="div" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      ) : null}
      {children}
    </Alert>
  );
}

Banner.propTypes = {
  kind: PropTypes.oneOf(["warn", "attention", "info", "done", "progress"]),
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  action: PropTypes.node,
  title: PropTypes.node,
  role: PropTypes.string,
  children: PropTypes.node,
  sx: PropTypes.object,
};
Banner.defaultProps = { kind: "info", icon: undefined, action: null, title: null, role: "note", children: null, sx: {} };

/**
 * 整列可點的清單列（取代 .el-row）。
 * <RowButton component={Link} to="/x" title="名稱" meta={<>…</>} side={<StatusChip …/>} />
 * tone="attention" / "warn" / "done" / "closed" 會在左邊畫一條紅／黃／綠／灰色帶。chevron 預設顯示（點了會走到別處）。
 * 需要完全自訂內容時，不傳 title / meta，直接放 children。
 */
export function RowButton({ title, meta, side, tone, chevron, children, sx, ...rest }) {
  return (
    <ListItemButton
      divider
      sx={{
        gap: 2,
        px: 3,
        py: 2,
        alignItems: "center",
        ...bandSx(tone),
        ...sx,
      }}
      {...rest}
    >
      {children || (
        <Box sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {title ? (
            <Typography variant="subtitle1" component="div">
              {title}
            </Typography>
          ) : null}
          {meta ? (
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}
            >
              {meta}
            </Typography>
          ) : null}
        </Box>
      )}
      {side ? <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: "0 0 auto" }}>{side}</Box> : null}
      {chevron ? (
        <Box component="span" aria-hidden="true" sx={{ display: "flex", color: "text.secondary", fontSize: "var(--icon)" }}>
          <MdChevronRight />
        </Box>
      ) : null}
    </ListItemButton>
  );
}

RowButton.propTypes = {
  title: PropTypes.node,
  meta: PropTypes.node,
  side: PropTypes.node,
  tone: PropTypes.oneOf(["", "attention", "warn", "done", "closed"]),
  chevron: PropTypes.bool,
  children: PropTypes.node,
  sx: PropTypes.object,
};
RowButton.defaultProps = { title: null, meta: null, side: null, tone: "", chevron: true, children: null, sx: {} };

/**
 * 進度條＋百分比（取代 .el-progress）。tone 決定顏色；ariaLabel 給讀螢幕念的完整說法（例如「專案進度 54%」）。
 */
const BAR = { progress: "primary", done: "success", attention: "error", warn: "warning", closed: "inherit" };

export function Progress({ value, tone, label, ariaLabel, sx }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ...sx }}>
      <LinearProgress
        variant="determinate"
        value={v}
        color={BAR[tone] || "primary"}
        sx={{ flex: "1 1 auto", minWidth: 96, bgcolor: "action.hover" }}
        aria-label={ariaLabel || (typeof label === "string" ? label : `${v}%`)}
      />
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
        {label ?? `${Math.round(v)}%`}
      </Typography>
    </Box>
  );
}

Progress.propTypes = {
  value: PropTypes.number,
  tone: PropTypes.oneOf(["progress", "done", "attention", "warn", "closed"]),
  label: PropTypes.node,
  ariaLabel: PropTypes.string,
  sx: PropTypes.object,
};
Progress.defaultProps = { value: 0, tone: "progress", label: undefined, ariaLabel: "", sx: {} };

/**
 * 收合區（取代 <details className="el-disclosure">）。
 * <Disclosure title="進階欄位" count="3 項" defaultOpen={false}>…</Disclosure>
 * 要受控就傳 open + onToggle(nextOpen)。summaryExtra 放在標頭右側（例如「展開／收合」字樣）。
 */
export function Disclosure({ title, count, summaryExtra, open, defaultOpen, onToggle, children, sx, ...rest }) {
  const controlled = open !== undefined;
  return (
    <Accordion
      {...(controlled ? { expanded: open } : { defaultExpanded: defaultOpen })}
      onChange={(_e, next) => onToggle && onToggle(next)}
      sx={sx}
      {...rest}
    >
      <AccordionSummary expandIcon={<MdExpandMore />}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
          <span>{title}</span>
          {count ? (
            <Typography variant="body2" color="text.secondary" component="span">
              {count}
            </Typography>
          ) : null}
        </Box>
        {summaryExtra ? (
          <Typography variant="body2" color="primary" component="span" sx={{ ml: "auto", mr: 1, alignSelf: "center" }}>
            {summaryExtra}
          </Typography>
        ) : null}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

Disclosure.propTypes = {
  title: PropTypes.node,
  count: PropTypes.node,
  summaryExtra: PropTypes.node,
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  children: PropTypes.node,
  sx: PropTypes.object,
};
Disclosure.defaultProps = { title: null, count: null, summaryExtra: null, open: undefined, defaultOpen: false, onToggle: null, children: null, sx: {} };

/** 工項狀態鍵（design/domain 的 pending / progress / review / done / closed）→ StatusChip kind */
export const TASK_STATUS_KIND = { pending: "closed", progress: "progress", review: "warn", done: "done", closed: "closed" };

/** 優先級鍵 → StatusChip kind */
export const PRIO_KIND = { urgent: "attention", high: "warn", medium: "closed", low: "closed" };

/**
 * 不能點的資料列（RowButton 的靜態版）：列裡自己有按鈕、或單純展示時用。
 * side 會換行，窄螢幕不會把動作擠出畫面。
 */
export function DataRow({ title, meta, side, tone, children, sx, ...rest }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 2,
        px: 3,
        py: 2,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-of-type": { borderBottom: 0 },
        ...bandSx(tone),
        ...sx,
      }}
      {...rest}
    >
      {children || (
        <Box sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {title ? (
            <Typography variant="subtitle1" component="div">
              {title}
            </Typography>
          ) : null}
          {meta ? (
            <Typography
              variant="body2"
              color="text.secondary"
              component="div"
              sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}
            >
              {meta}
            </Typography>
          ) : null}
        </Box>
      )}
      {side ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, flex: "0 1 auto" }}>{side}</Box>
      ) : null}
    </Box>
  );
}

DataRow.propTypes = {
  title: PropTypes.node,
  meta: PropTypes.node,
  side: PropTypes.node,
  tone: PropTypes.oneOf(["", "attention", "warn", "done", "closed"]),
  children: PropTypes.node,
  sx: PropTypes.object,
};
DataRow.defaultProps = { title: null, meta: null, side: null, tone: "", children: null, sx: {} };

/** 空狀態：標題＋說明＋（可選）動作。dense 用在卡片裡，上下留白小一點。 */
export function EmptyState({ title, text, action, dense, sx }) {
  return (
    <Box sx={{ textAlign: "center", py: dense ? 4 : 8, px: 2, ...sx }}>
      {title ? (
        <Typography variant={dense ? "h3" : "h2"} component="p">
          {title}
        </Typography>
      ) : null}
      {text ? <Typography sx={{ mt: 1, maxWidth: "34em", mx: "auto" }}>{text}</Typography> : null}
      {action ? <Box sx={{ mt: 3 }}>{action}</Box> : null}
    </Box>
  );
}

EmptyState.propTypes = { title: PropTypes.node, text: PropTypes.node, action: PropTypes.node, dense: PropTypes.bool, sx: PropTypes.object };
EmptyState.defaultProps = { title: null, text: null, action: null, dense: false, sx: {} };

/** 欄位錯誤句子：警告圖示＋內文字級，讀螢幕靠 id 被 aria-describedby 指到 */
export function FieldError({ id, children }) {
  if (!children) return null;
  return (
    <FormHelperText error id={id || undefined} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, fontSize: "var(--t-body)", fontWeight: 600 }}>
      <Box component="span" aria-hidden="true" sx={{ display: "flex", pt: "0.2em" }}>
        <MdWarningAmber />
      </Box>
      {children}
    </FormHelperText>
  );
}

FieldError.propTypes = { id: PropTypes.string, children: PropTypes.node };
FieldError.defaultProps = { id: "", children: null };

/**
 * 表單欄位外框：上方粗體標籤（必填／選填標記）＋欄位本體＋下方說明＋錯誤。
 * 說明一律放在輸入框下方（跟 MUI TextField 的 helperText 同一個位置）。
 * 欄位本體（TextField 等）放 children，標籤用 htmlFor 指到它的 id。
 * mark："req"＝必填（紅）、"opt"＝選填、"suggest"＝建議填寫、"ro"＝系統自動產生，不傳就不標。
 */
const MARK = {
  req: { text: "必填", color: "error.main" },
  opt: { text: "選填", color: "text.secondary" },
  suggest: { text: "建議填寫", color: "warning.dark" },
  ro: { text: "系統自動產生", color: "text.secondary" },
};

export function Field({ label, htmlFor, mark, help, helpId, error, errorId, footer, children, sx }) {
  const m = MARK[mark];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, ...sx }}>
      {label ? (
        <Typography
          component={htmlFor ? "label" : "span"}
          htmlFor={htmlFor || undefined}
          sx={{ fontSize: "var(--t-section)", lineHeight: "var(--t-section-lh)", fontWeight: 700 }}
        >
          {label}
          {m ? (
            <Typography component="span" variant="body2" sx={{ color: m.color, fontWeight: 600, ml: 1 }}>
              {m.text}
            </Typography>
          ) : null}
        </Typography>
      ) : null}
      {children}
      {help ? (
        <Typography id={helpId || undefined} variant="body2" color="text.secondary">
          {help}
        </Typography>
      ) : null}
      <FieldError id={errorId}>{error}</FieldError>
      {footer}
    </Box>
  );
}

Field.propTypes = {
  label: PropTypes.node,
  htmlFor: PropTypes.string,
  mark: PropTypes.oneOf(["", "req", "opt", "suggest", "ro"]),
  help: PropTypes.node,
  helpId: PropTypes.string,
  error: PropTypes.node,
  errorId: PropTypes.string,
  footer: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
};
Field.defaultProps = { label: null, htmlFor: "", mark: "", help: null, helpId: "", error: null, errorId: "", footer: null, children: null, sx: {} };

/** 帶放大鏡的搜尋框。沒給 label 時用 ariaLabel（再沒有就用 placeholder）當讀螢幕名稱；slotProps 會合併進去。 */
export function SearchField({ value, onChange, placeholder, label, ariaLabel, inputRef, slotProps, sx, ...rest }) {
  return (
    <TextField
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      label={label || undefined}
      inputRef={inputRef}
      sx={sx}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <MdSearch aria-hidden="true" />
            </InputAdornment>
          ),
        },
        ...slotProps,
        htmlInput: {
          ...(label ? {} : { "aria-label": ariaLabel || placeholder }),
          ...(slotProps && slotProps.htmlInput),
        },
      }}
      {...rest}
    />
  );
}

SearchField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  slotProps: PropTypes.object,
  sx: PropTypes.object,
};
SearchField.defaultProps = {
  value: "",
  onChange: () => {},
  placeholder: "搜尋…",
  label: "",
  ariaLabel: "",
  inputRef: undefined,
  slotProps: {},
  sx: {},
};

/** 使用者頭像：依姓名取色、尺寸跟著字級（size 是 em 倍數）。 */
export function UserAvatar({ name, size, sx, ...rest }) {
  return (
    <Avatar
      alt={name}
      sx={{
        width: `${size}em`,
        height: `${size}em`,
        fontSize: `calc(var(--t-meta) * ${0.32 * size})`,
        fontWeight: 700,
        color: "#fff",
        bgcolor: avatarColor(name),
        flexShrink: 0,
        ...sx,
      }}
      {...rest}
    >
      {initialOf(name)}
    </Avatar>
  );
}

UserAvatar.propTypes = { name: PropTypes.string, size: PropTypes.number, sx: PropTypes.object };
UserAvatar.defaultProps = { name: "", size: 2.34, sx: {} };

/**
 * 密碼欄位：TextField 右側多一顆「顯示／隱藏密碼」的眼睛按鈕。
 * 其餘 props（id、value、onChange、placeholder、autoComplete、error、inputRef、slotProps…）原樣傳給 TextField。
 * 按鈕用 onMouseDown 擋掉預設行為，點了游標不會跑出輸入框。
 */
export function PasswordField({ slotProps, ...rest }) {
  const [show, setShow] = useState(false);
  const label = show ? "隱藏密碼" : "顯示密碼";
  return (
    <TextField
      {...rest}
      type={show ? "text" : "password"}
      slotProps={{
        ...slotProps,
        input: {
          ...(slotProps && slotProps.input),
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={label}>
                <IconButton
                  edge="end"
                  aria-label={label}
                  aria-pressed={show}
                  onClick={() => setShow((v) => !v)}
                  onMouseDown={(e) => e.preventDefault()}
                  sx={{ width: "var(--hit-min)", height: "var(--hit-min)" }}
                >
                  {show ? <MdVisibilityOff /> : <MdVisibility />}
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

PasswordField.propTypes = { slotProps: PropTypes.object };
PasswordField.defaultProps = { slotProps: {} };
